import React from "react";
import * as MUI from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmAndCancelButtons from "../../../../components/buttons/ConfirmAndCancelButtons";
import useSubmit from "../../../master/species/hooks/useSubmit";
import usePut from "../../../../hooks/usePut";
import * as apiPaths from "../../../../enums/ApiPaths";
import * as keys from "../../../../enums/QueryKeys";

const SHORT_FIELD_MAX_LENGTH = 255;
const LONG_FIELD_MAX_LENGTH = 2000;

export default function PrescriptionTemplateFormDialog({ open, onClose, clinicId, initialValues }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const isEditMode = Boolean(initialValues);

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm({
    defaultValues: {
      title: initialValues?.title ?? "",
      manufacturerName: initialValues?.manufacturerName ?? "",
      genericName: initialValues?.genericName ?? "",
      descriptionWhy: initialValues?.descriptionWhy ?? "",
      descriptionHowToApply: initialValues?.descriptionHowToApply ?? "",
      karenca: initialValues?.karenca ?? "",
      dispensingCode: initialValues?.dispensingCode ?? "",
    },
  });

  const templateUrl = `${apiPaths.PRESCRIPTION_TEMPLATE}${clinicId}`;
  const create = useSubmit(null, templateUrl, null, [keys.PRESCRIPTION_TEMPLATES_LIST + clinicId]);
  const edit = usePut(
    templateUrl,
    [keys.PRESCRIPTION_TEMPLATES_LIST + clinicId],
    [keys.PRESCRIPTION_TEMPLATES_LIST + clinicId],
  );

  const submitData = async (data) => {
    const obj = isEditMode ? { ...initialValues, ...data } : data;
    const success = isEditMode
      ? await edit.submitCallback(obj)
      : await create.submitCallback(obj);
    if (success) {
      onClose();
    }
  };

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.Stack direction="row" justifyContent="flex-end" sx={{ p: 1 }}>
        <MUI.IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Stack>
      <MUI.DialogContent
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          pt: 0,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "grey.50" : "rgba(255,255,255,0.02)",
        }}
      >
        <form onSubmit={handleSubmit(submitData)}>
          <MUI.Stack spacing={3}>
            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("Title")} *
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t("PrescriptionTemplateTitlePlaceholder")}
                inputProps={{ maxLength: 120 }}
                {...register("title", { required: t("EmptyField"), maxLength: { value: 120 } })}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </MUI.Box>

            <MUI.Box
              sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
            >
              <MUI.Box sx={{ flex: 1 }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("ManufacturerName")}
                </MUI.Typography>
                <MUI.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("ManufacturerNamePlaceholder")}
                  inputProps={{ maxLength: SHORT_FIELD_MAX_LENGTH }}
                  {...register("manufacturerName")}
                />
              </MUI.Box>
              <MUI.Box sx={{ flex: 1 }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("GenericName")}
                </MUI.Typography>
                <MUI.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("GenericNamePlaceholder")}
                  inputProps={{ maxLength: SHORT_FIELD_MAX_LENGTH }}
                  {...register("genericName")}
                />
              </MUI.Box>
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("DescriptionWhy")}
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                placeholder={t("DescriptionWhyPlaceholder")}
                inputProps={{ maxLength: LONG_FIELD_MAX_LENGTH }}
                {...register("descriptionWhy")}
              />
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("DescriptionHowToApply")}
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                placeholder={t("DescriptionHowToApplyPlaceholder")}
                inputProps={{ maxLength: LONG_FIELD_MAX_LENGTH }}
                {...register("descriptionHowToApply")}
              />
            </MUI.Box>

            <MUI.Box
              sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
            >
              <MUI.Box sx={{ flex: 1 }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("Karenca")}
                </MUI.Typography>
                <MUI.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("KarencaPlaceholder")}
                  inputProps={{ maxLength: SHORT_FIELD_MAX_LENGTH }}
                  {...register("karenca")}
                />
              </MUI.Box>
              <MUI.Box sx={{ flex: 1 }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("DispensingCode")}
                </MUI.Typography>
                <MUI.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("DispensingCodePlaceholder")}
                  inputProps={{ maxLength: SHORT_FIELD_MAX_LENGTH }}
                  {...register("dispensingCode")}
                />
              </MUI.Box>
            </MUI.Box>

            <MUI.Divider />
            <MUI.Typography variant="caption" color="error">
              {`* ${t("RequiredFields")}`}
            </MUI.Typography>

            <ConfirmAndCancelButtons
              useCustomLoadingButtonOnConfirm={true}
              isConfirmLoading={isEditMode ? edit.loading : create.loading}
              confirmTitle="Save"
              cancelTitle=""
              confirmButtonType="submit"
            />
          </MUI.Stack>
        </form>
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
